/* every published penalties error code mapped to a named handler. */
import { api_error } from '../format';
import { ERROR } from './params';

export const KIND = {
  FIX: 'fix',
  STALE: 'stale',
  ADOPT: 'adopt',
  CONFIRM: 'confirm',
  ALREADY_SAVED: 'already_saved',
  PERMISSION: 'permission',
  UNCERTAIN: 'uncertain'
};

// confirm: the request flag a CONFIRM handler resends with.
export const HANDLERS = {
  [ERROR.INVALID_REQUEST]: {
    kind: KIND.FIX,
    text: 'The request was incomplete. Close this dialog, reload the page and try again.'
  },
  [ERROR.INVALID_NOTE]: { kind: KIND.FIX, text: 'Enter a note of 3 to 1,024 characters.' },
  [ERROR.INVALID_PCT]: { kind: KIND.FIX, text: 'Enter a percentage from 0 to 150.' },
  [ERROR.INVALID_AMOUNT]: { kind: KIND.FIX, text: 'Enter a valid amount in rupees.' },
  [ERROR.PCT_AMOUNT_MISMATCH]: {
    kind: KIND.FIX,
    text: 'The percentage and the amount disagree. Enter only one of them.'
  },
  [ERROR.INVALID_PENALTY_TYPE]: { kind: KIND.FIX, text: 'Choose a penalty type from the list.' },
  [ERROR.INVALID_REASON_CODE]: { kind: KIND.FIX, text: 'Choose a reason from the list.' },
  [ERROR.INVALID_APPROVED_BY]: {
    kind: KIND.FIX,
    text: '"Approved by" must be at most 64 characters.'
  },
  [ERROR.MIXED_EDIT]: {
    kind: KIND.FIX,
    text: 'A correction and a waiver are saved separately. Save the correction first, then waive.'
  },
  [ERROR.SESSION_REQUIRED]: {
    kind: KIND.FIX,
    text: 'Pick the session, or choose "Manual (month-level)" for a penalty with no session.'
  },
  [ERROR.BULK_TOO_LARGE]: { kind: KIND.FIX, text: 'Select at most 200 penalties at a time.' },
  [ERROR.INVALID_DIRECTIVE]: {
    kind: KIND.FIX,
    text: 'That Free Pass change is not valid. Close this dialog and try again.'
  },
  [ERROR.FORBIDDEN]: { kind: KIND.PERMISSION, text: "You don't have access to teacher payments." },
  [ERROR.PENALTY_NOT_FOUND]: {
    kind: KIND.STALE,
    text: 'This penalty no longer exists. The list has been refreshed.'
  },
  [ERROR.SESSION_NOT_FOUND]: {
    kind: KIND.FIX,
    text: 'That session could not be found. Pick it again.'
  },
  [ERROR.TEACHER_NOT_FOUND]: {
    kind: KIND.FIX,
    text: 'That teacher could not be found. Pick them again.'
  },
  [ERROR.MONTH_CLOSED]: {
    kind: KIND.STALE,
    text: "This teacher's month is closed. Reopen it under Needs attention first."
  },
  [ERROR.MONTH_NOT_OPEN]: {
    kind: KIND.STALE,
    text: "This teacher's month is not open, so its penalties cannot change."
  },
  [ERROR.MONTH_OUTSIDE_WINDOW]: {
    kind: KIND.FIX,
    text: 'A month-level penalty can only go in the current or the previous month.'
  },
  [ERROR.PREVIOUS_MONTH_NO_RECORD]: {
    kind: KIND.FIX,
    text: 'This teacher has no payment record for that month. Add the penalty to the current month instead.'
  },
  [ERROR.VERSION_CONFLICT]: {
    kind: KIND.ADOPT,
    text: 'This penalty changed since it was loaded. Its current values are shown; review them and try again.'
  },
  [ERROR.PENALTY_PASS_COVERED]: {
    kind: KIND.STALE,
    text: 'This penalty changed since it was loaded. The list has been refreshed.'
  },
  // an orphan charges nothing; only Revert changes it.
  [ERROR.PENALTY_ORPHANED]: {
    kind: KIND.STALE,
    text: 'This penalty no longer matches a session. Revert it or leave it; it is not charged.'
  },
  [ERROR.NOT_OVERRIDDEN]: {
    kind: KIND.STALE,
    text: 'This penalty already has the system value, so there is nothing to revert.'
  },
  [ERROR.MANUAL_NOT_REVERTIBLE]: {
    kind: KIND.STALE,
    text: 'An Ops-added penalty has no system value to revert to. Remove it instead.'
  },
  [ERROR.SYSTEM_NOT_REMOVABLE]: {
    kind: KIND.STALE,
    text: 'A system penalty cannot be removed. Waive it or correct it instead.'
  },
  [ERROR.SESSION_NOT_OWNED]: {
    kind: KIND.FIX,
    text: 'That session does not belong to the selected teacher.'
  },
  [ERROR.FREE_PASS_NOT_ON_DAY]: {
    kind: KIND.STALE,
    text: 'That day no longer holds a Free Pass. The Free Passes have been refreshed.'
  },
  [ERROR.FREE_PASS_DAY_NOT_ELIGIBLE]: {
    kind: KIND.FIX,
    text: 'That day has no penalty a Free Pass can cover. Choose another day.'
  },
  [ERROR.FREE_PASS_ALREADY_ON_DAY]: {
    kind: KIND.FIX,
    text: 'That day already holds a Free Pass. Choose another day.'
  },
  [ERROR.FREE_PASS_NO_BALANCE]: {
    kind: KIND.FIX,
    text: 'The teacher does not have enough Free Passes for this.'
  },
  [ERROR.FREE_PASS_TYPE_NOT_ALLOWED]: {
    kind: KIND.FIX,
    text: 'A Free Pass Day cannot cover a late join or early exit penalty.'
  },
  [ERROR.CONFIRM_DISPLACE_REQUIRED]: {
    kind: KIND.CONFIRM,
    confirm: 'confirm_displace',
    text: 'This takes the automatic Free Pass from another day. Confirm to continue.'
  },
  [ERROR.DIRECTIVE_CHANGED]: {
    kind: KIND.ADOPT,
    text: 'The Free Passes changed since you opened them. The current ones are shown; review them and try again.'
  },
  [ERROR.CONFIRM_LARGE_REQUIRED]: {
    kind: KIND.CONFIRM,
    confirm: 'confirm_large',
    text: 'This amount is above the review threshold. Confirm to save it.'
  },
  [ERROR.POSSIBLE_DUPLICATE]: {
    kind: KIND.CONFIRM,
    confirm: 'confirm_duplicate',
    text: 'The system already charged this penalty type on this session. Confirm to add another.'
  },
  [ERROR.IDEMPOTENCY_MISMATCH]: {
    kind: KIND.ALREADY_SAVED,
    text: 'Your earlier add of this penalty went through with different details and was not added again. Find it in the list.'
  },
  [ERROR.LOOKUP_FAILED]: {
    kind: KIND.UNCERTAIN,
    text: 'Something went wrong on the server. Try again.'
  },
  [ERROR.BAD_SORT]: {
    kind: KIND.FIX,
    text: 'That sort is not available. Reload the page and try again.'
  }
};

// A write that got no answer, or a 5xx, may have committed; a resend is refused or replayed, never doubled.
export const UNCERTAIN_WRITE_TEXT =
  'The change may have been saved. The list has been reloaded; check the penalty before trying again.';
export const UNCERTAIN_ADD_TEXT =
  'The penalty may have been added. Press Add again: it will not be added twice.';

export const REQUEST_FAILED_TEXT = 'The request failed. Try again.';

const field_messages = (fields) =>
  (Array.isArray(fields) ? fields : []).reduce((acc, f) => {
    if (f && f.field && !acc[f.field])
      acc[f.field] = f.message || (HANDLERS[f.code] || {}).text || '';
    return acc;
  }, {});

/*
 * The one reader for a failed penalties request. write=true marks a mutation, whose 5xx or dropped
 * answer is UNCERTAIN; add=true picks the replay-safe wording.
 */
export const error_view = (e, { write = false, add = false } = {}) => {
  const res = (e && e.response) || {};
  const body = res.data || {};
  const { status } = res;
  const { code, message } = api_error(e);
  const handler = HANDLERS[code];
  const no_answer = !status;
  const is_permission = status === 401 || status === 403;
  let kind = handler ? handler.kind : KIND.FIX;
  if (is_permission) kind = KIND.PERMISSION;
  else if (no_answer || status >= 500) kind = write ? KIND.UNCERTAIN : KIND.FIX;
  let text = message || (handler && handler.text) || REQUEST_FAILED_TEXT;
  if (kind === KIND.UNCERTAIN && write) text = add ? UNCERTAIN_ADD_TEXT : UNCERTAIN_WRITE_TEXT;
  return {
    code,
    status,
    kind,
    text,
    confirm: (handler && handler.confirm) || '',
    fields: field_messages(body.fields),
    current: body.current && typeof body.current === 'object' ? body.current : null,
    free_pass: body.free_pass && typeof body.free_pass === 'object' ? body.free_pass : null,
    displaced: Array.isArray(body.displaced) ? body.displaced : [],
    matches: Array.isArray(body.matches) ? body.matches : [],
    existing: body.existing && typeof body.existing === 'object' ? body.existing : null,
    mismatch_fields: Array.isArray(body.mismatch_fields) ? body.mismatch_fields : [],
    suggested_pay_month:
      typeof body.suggested_pay_month === 'string' ? body.suggested_pay_month : ''
  };
};

// The dialog's submit stays off after these until the operator closes it (the list reloads behind it).
export const blocks_submit = (view) =>
  !!view && (view.kind === KIND.STALE || view.kind === KIND.ALREADY_SAVED);

// The list behind the dialog is out of date after these.
export const reloads_list = (view) =>
  !!view && [KIND.STALE, KIND.ADOPT, KIND.ALREADY_SAVED, KIND.UNCERTAIN].indexOf(view.kind) !== -1;

// a month-level add refused for its month offers the server's suggested month ("YYYY-MM").
export const suggested_month = (view) =>
  !!view &&
  (view.code === ERROR.MONTH_OUTSIDE_WINDOW || view.code === ERROR.PREVIOUS_MONTH_NO_RECORD) &&
  /^\d{4}-\d{2}$/.test(view.suggested_pay_month)
    ? view.suggested_pay_month
    : '';
